import React from 'react'
import { Container, Col, Row } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'

import { IMainState } from 'store/states'
import Button, {
  ButtonVariant,
  ButtonSizes,
} from 'design-system/button/src/Button'
import EvaluationDetails from './components/evaluation-details/EvaluationDetails'
import EvaluationQuestions from './components/evaluation-questions/EvaluationQuestions'
import EvaluationConfig from './components/evaluation-config/EvaluationConfig'
import { useSaveQuestions } from './contexts/SaveQuestions'
import styles from './Evaluation.module.scss'
import { Buttons, ActionsOnEdit, Confirmation } from './components'
import { EvaluationSteps, ModalTypes } from './constants'
import { isLastStep } from './utils'
import { UploadQuestion } from './UploadQuestion'

export interface EvaluationProps {
  onClose: () => void
  editing: boolean
  containerRef: React.RefObject<HTMLDivElement>
}

const Evaluation: React.FC<EvaluationProps> = ({ onClose, editing }) => {
  const { t } = useTranslation()

  const { edit } = useSelector(({ blocks }: IMainState) => blocks)

  const {
    data,
    setData,
    editedQuestions,
    setQuestionsWeight,
    step,
    setChanges,
    onSaveAndChangeStep,
    save,
    canShowConfirmation,
  } = useSaveQuestions()

  const [show, setShow] = React.useState<ModalTypes | null>(null)

  const onCloseModal = () => {
    setShow(null)
  }

  const handleNext = () => {
    onSaveAndChangeStep(step + 1)
  }

  const onSaveAndClose = (options?: { uploadVersion?: boolean }) => {
    save({
      uploadVersion: options?.uploadVersion,
    })
    onClose()
  }

  const onFinish = () => {
    if (
      canShowConfirmation({
        validAll: true,
      })
    ) {
      setShow(ModalTypes.CONFIRMATION)
      return
    }
    onSaveAndClose()
  }

  const lastStep = isLastStep(step)
  return (
    <div className="w-100">
      {!!(edit.id && edit.instanceId) && (
        <div className="d-flex justify-content-end">
          <UploadQuestion evaluationId={edit.id} instanceId={edit.instanceId} />
        </div>
      )}
      <Container id="evaluation-container" className={styles.container}>
        <Row>
          <Col xs={3} className={'d-flex flex-column border-right'}>
            <h1 className={styles.title}>
              {t(
                `administration.blocks.type.evaluation.${
                  editing ? 'edit' : 'new'
                }`
              )}
            </h1>
            <Buttons />
          </Col>
          <Col className={'ml-4 '}>
            <div className={styles.stepContainer}>
              {step === EvaluationSteps.DETAILS && (
                <EvaluationDetails
                  onEditingData={data.details}
                  onChange={(newData) => {
                    setData({ ...data, details: newData })
                    setChanges(step)
                  }}
                />
              )}
              {step === EvaluationSteps.QUESTIONS && (
                <EvaluationQuestions
                  id={edit.id || 0}
                  instanceId={edit.instanceId || 0}
                  onAnyChange={() => {
                    setChanges(step)
                  }}
                />
              )}
              {step === EvaluationSteps.CONFIG && (
                <EvaluationConfig
                  optionsData={data.config}
                  onChange={(newData) => {
                    setData({ ...data, config: newData })
                    setChanges(step)
                  }}
                  instanceId={edit.instanceId || 0}
                  editedQuestions={editedQuestions}
                  onQuestionWeightChange={(data) => {
                    setQuestionsWeight(data)
                    setChanges(step)
                  }}
                />
              )}
            </div>
            <div>
              <Row>
                <div id="buttons-container" className={styles.buttonsContainer}>
                  <Button
                    onClick={onClose}
                    variant={ButtonVariant.Secondary}
                    size={ButtonSizes.Small}
                  >
                    {t('administration.blocks.type.evaluation.buttons.cancel')}
                  </Button>
                  <Button
                    className={'mr-0'}
                    size={ButtonSizes.Small}
                    onClick={() => {
                      if (lastStep) onFinish()
                      else handleNext()
                    }}
                  >
                    {t(
                      `administration.blocks.type.evaluation.buttons.${
                        lastStep ? 'finish' : 'next'
                      }`
                    )}
                  </Button>
                </div>
              </Row>
            </div>
          </Col>
        </Row>
        <ActionsOnEdit
          show={show === ModalTypes.OPTIONS}
          onFinish={() => {
            // This increment the version of the evaluation
            onSaveAndClose({
              uploadVersion: true,
            })
          }}
          onClose={onCloseModal}
        />
        <Confirmation
          show={show === ModalTypes.CONFIRMATION}
          onAccept={() => {
            setShow(ModalTypes.OPTIONS)
          }}
          onClose={onCloseModal}
        />
      </Container>
    </div>
  )
}

export default Evaluation
